import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { DangerZone } from "@/components/layout/danger-zone";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

export type ScenarioCriticalityOption =
  | "critical_path"
  | "important"
  | "optional";

export function normalizeCriticality(
  value: string | null | undefined,
): ScenarioCriticalityOption {
  if (value === "critical_path" || value === "critical") return "critical_path";
  if (value === "optional" || value === "low") return "optional";
  return "important";
}

export interface EditScenarioDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  name: string;
  primaryCapabilityId: string | null;
  taskCriticality: string | null | undefined;
  narrative: string;
  tension: string;
  humanTests: Array<{ id: string; question: string }>;
  capabilities: Array<{ id: string; name: string }>;
  isSaving: boolean;
  onSave: (input: {
    name: string;
    primaryCapabilityId?: string;
    taskCriticality: ScenarioCriticalityOption;
    narrative: string | null;
    tension: string | null;
    humanTests?: string[];
  }) => void;
}

export function EditScenarioDialog({
  open,
  onOpenChange,
  name,
  primaryCapabilityId,
  taskCriticality,
  narrative,
  tension,
  humanTests,
  capabilities,
  isSaving,
  onSave,
}: EditScenarioDialogProps) {
  const [nameDraft, setNameDraft] = useState("");
  const [capabilityId, setCapabilityId] = useState("");
  const [criticality, setCriticality] =
    useState<ScenarioCriticalityOption>("important");
  const [narrativeDraft, setNarrativeDraft] = useState("");
  const [tensionDraft, setTensionDraft] = useState("");
  const [questions, setQuestions] = useState<string[]>([]);

  useEffect(() => {
    if (!open) return;
    setNameDraft(name);
    setCapabilityId(primaryCapabilityId ?? capabilities[0]?.id ?? "");
    setCriticality(normalizeCriticality(taskCriticality));
    setNarrativeDraft(narrative);
    setTensionDraft(tension);
    setQuestions(humanTests.map((ht) => ht.question));
  }, [
    open,
    name,
    primaryCapabilityId,
    taskCriticality,
    narrative,
    tension,
    humanTests,
    capabilities,
  ]);

  const canSave =
    !isSaving &&
    nameDraft.trim().length > 0 &&
    narrativeDraft.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit scenario</DialogTitle>
          <DialogDescription>
            Update the scenario title, capability, criticality, shared
            situation, and human tests.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="edit-scenario-name">Name</Label>
            <Input
              id="edit-scenario-name"
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              placeholder="e.g. First expense claim"
              required
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="min-w-0 space-y-2">
              <Label htmlFor="edit-scenario-capability">Capability</Label>
              <Select value={capabilityId} onValueChange={setCapabilityId}>
                <SelectTrigger
                  id="edit-scenario-capability"
                  className="w-full min-w-0 max-w-full overflow-hidden"
                >
                  <SelectValue placeholder="Select a capability" />
                </SelectTrigger>
                <SelectContent
                  position="popper"
                  className="w-[var(--radix-select-trigger-width)] max-w-[calc(100vw-2rem)]"
                >
                  {capabilities.map((cap) => (
                    <SelectItem key={cap.id} value={cap.id}>
                      {cap.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="min-w-0 space-y-2">
              <Label htmlFor="edit-scenario-criticality">Criticality</Label>
              <Select
                value={criticality}
                onValueChange={(v) =>
                  setCriticality(v as ScenarioCriticalityOption)
                }
              >
                <SelectTrigger
                  id="edit-scenario-criticality"
                  className="w-full min-w-0 max-w-full overflow-hidden"
                >
                  <SelectValue placeholder="Select criticality" />
                </SelectTrigger>
                <SelectContent
                  position="popper"
                  className="w-[var(--radix-select-trigger-width)] max-w-[calc(100vw-2rem)]"
                >
                  <SelectItem value="critical_path">Critical path</SelectItem>
                  <SelectItem value="important">Important</SelectItem>
                  <SelectItem value="optional">Optional</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-scenario-narrative">Narrative</Label>
            <Textarea
              id="edit-scenario-narrative"
              value={narrativeDraft}
              onChange={(e) => setNarrativeDraft(e.target.value)}
              rows={6}
              className="leading-relaxed"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-scenario-tension">Tension</Label>
            <Input
              id="edit-scenario-tension"
              value={tensionDraft}
              onChange={(e) => setTensionDraft(e.target.value)}
              placeholder="e.g. Speed of automation vs. audit-grade accuracy"
            />
          </div>

          <div className="space-y-2">
            <Label>Human tests</Label>
            <div className="space-y-2">
              {questions.map((q, i) => (
                <div key={i} className="flex items-start gap-2">
                  <Input
                    value={q}
                    onChange={(e) =>
                      setQuestions((prev) =>
                        prev.map((item, j) =>
                          j === i ? e.target.value : item,
                        ),
                      )
                    }
                    placeholder="e.g. Could they submit the claim without asking anyone?"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setQuestions((p) => p.filter((_, j) => j !== i))
                    }
                    aria-label={`Remove human test ${i + 1}`}
                    className="mt-2 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground hover:text-destructive"
                  >
                    <span aria-hidden="true">×</span>
                  </button>
                </div>
              ))}
              {questions.length < 3 && (
                <button
                  type="button"
                  onClick={() => setQuestions((p) => [...p, ""])}
                  className="inline-flex items-center gap-1 text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                >
                  <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                  Add human test
                </button>
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              const cleaned = questions.map((q) => q.trim()).filter(Boolean);
              onSave({
                name: nameDraft.trim(),
                primaryCapabilityId: capabilityId || undefined,
                taskCriticality: criticality,
                narrative: narrativeDraft.trim() || null,
                tension: tensionDraft.trim() || null,
                humanTests: cleaned.length > 0 ? cleaned : undefined,
              });
            }}
            disabled={!canSave}
          >
            {isSaving ? "Saving…" : "Save scenario"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ScenarioDangerZone({
  outlineId,
  projectId,
}: {
  outlineId: string;
  projectId: string;
}) {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const deleteMutation = trpc.outline.delete.useMutation({
    onSuccess: () => {
      toast.success("Scenario deleted");
      void utils.outline.list.invalidate();
      navigate(`/projects/${projectId}/scenarios`);
    },
    onError: (err) => toastTrpcError(err, "Failed to delete scenario"),
  });
  return (
    <DangerZone
      title="Delete scenario"
      description="Deleting a scenario removes every cast persona's walkthroughs, findings, and briefs tied to it."
      actionLabel="Delete scenario"
      confirmTitle="Delete this scenario?"
      confirmDescription="Removes the situation, every cast persona binding, and all walkthroughs, findings, and briefs underneath. This cannot be undone."
      onConfirm={() => deleteMutation.mutate({ id: outlineId })}
      isPending={deleteMutation.isPending}
    />
  );
}
